import { motion } from "framer-motion";
import { Star, Quotes, GoogleLogo } from "@phosphor-icons/react";
import { REVIEWS, GOOGLE_RATING, REVIEWS_URL } from "@/data/content";

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function Reviews() {
  return (
    <section id="reviews" className="py-16 md:py-32 bg-background" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Customer Reviews</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary">
            Service focused on customer satisfaction
          </h2>
        </div>

        {/* Google rating summary */}
        <a
          href={REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-border bg-card px-5 py-4 mb-12 shadow-sm hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer"
          data-testid="google-rating"
        >
          <GoogleLogo size={28} weight="bold" className="text-primary shrink-0" />
          <span className="font-heading text-3xl font-medium text-primary leading-none">{GOOGLE_RATING.score}</span>
          <span className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, s) => (
              <Star key={s} size={20} weight="fill" className="text-amber-400" />
            ))}
          </span>
          <span className="text-foreground/70 font-medium">
            {GOOGLE_RATING.count} Google Reviews
          </span>
        </a>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {REVIEWS.map((r, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="rounded-3xl border border-border bg-card p-7 relative flex flex-col"
              data-testid={`review-${i}`}
            >
              <Quotes size={36} weight="fill" className="text-secondary absolute top-6 right-6" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={17} weight="fill" className="text-amber-400" />
                ))}
              </div>
              <p className="text-primary leading-relaxed flex-1">{r.text}</p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-border">
                <span className="w-10 h-10 rounded-full bg-secondary text-primary font-heading font-semibold flex items-center justify-center text-sm shrink-0">
                  {initials(r.author)}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-primary text-sm truncate">{r.author}</p>
                  <p className="flex items-center gap-1 text-xs text-foreground/50">
                    <GoogleLogo size={12} weight="bold" /> Google Review
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
